import {
	CircularProgress,
	type PaletteMode,
	type PaletteOptions,
	type Theme,
} from "@mui/material";
import {
	alpha,
	createTheme,
	darken,
	lighten,
	type ThemeOptions,
} from "@mui/material/styles";
import { csCZ } from "@mui/x-date-pickers/locales";
import { createElement } from "react";
import type { GetThemeFunctionType } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import {
	Colors,
	DarkModeColors,
	DarkPortfolioAreaPalette,
	DarkScales,
	DarkSurfaces,
	type HspThemeColors,
	PortfolioAreaPalette,
	Scales,
	Surfaces,
} from "web_myplann/Infrastructure/Themes/Colors";
import type {
	CalloutAppearance,
	CalloutColor,
} from "web_myplann/Shared/Types/CalloutAppearance";
import "@mui/x-date-pickers/themeAugmentation";

const SCROLLBAR_SIZE_PX = 6;
const SCROLLBAR_RADIUS_PX = 8;
const OVERLAY_SCROLLBAR_SIZE_PX = 4;
const SCROLLBAR_THUMB_COLOR = "rgba(158, 158, 158, 0.15)";
const SCROLLBAR_THUMB_COLOR_HOVER = "rgba(158, 158, 158, 0.30)";
const solidBorder = (color: string) => `1px solid ${color}`;
const PILL_RADIUS = "9999px";

const iconBoxSize = {
	sm: 32,
	md: 40,
};

const softAvatarBase = {
	width: iconBoxSize.md,
	height: iconBoxSize.md,
	borderRadius: "10px",
} as const;

const calloutCardBase = {
	boxShadow: "none",
	transition: "transform 200ms",
	"&:has(.MuiCardActionArea-root:active)": { transform: "scale(0.99)" },
} as const;

const radii = {
	none: "0",
	sm: "4px",
	md: "8px",
	lg: "12px",
	xl: "16px",
	full: PILL_RADIUS,
};

const iconSize = {
	xs: 14,
	sm: 16,
	md: 20,
	lg: 24,
};

const theme = (color: HspThemeColors, variant: PaletteMode): Theme => {
	const scales = variant === "dark" ? DarkScales : Scales;
	const surfaces = variant === "dark" ? DarkSurfaces : Surfaces;
	const elevatedShadow =
		variant === "dark"
			? "0 1px 3px rgba(0, 0, 0, 0.5), 0 1px 2px rgba(0, 0, 0, 0.3)"
			: "0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)";
	const palette = {
		mode: variant,
		primary: {
			light: color.hspColor25 || color.Primary25,
			main: color.hspColor || color.Primary,
			dark: color.hspColor75 || color.Primary75,
			contrastText: color.White,
		},
		secondary: {
			light: color.Secondary25,
			main: color.Secondary,
			dark: color.Secondary75,
			contrastText: color.Black,
		},
		text: {
			primary: color.Black,
			secondary: color.MonoChrome500,
			disabled: color.MonoChrome400,
		},
		error: {
			light: scales.red[100],
			main: scales.red[600],
			dark: scales.red[800],
			contrastText: color.White,
		},
		success: {
			light: scales.green[100],
			main: scales.green[600],
			dark: scales.green[800],
			contrastText: color.White,
		},
		info: {
			light: scales.blue[100],
			main: scales.blue[600],
			dark: scales.blue[800],
			contrastText: color.White,
		},
		background: {
			default: color.White,
			paper: surfaces.paper,
			appBar: surfaces.appBar,
		},
		surface: {
			muted: scales.grey[50],
			border:
				variant === "light"
					? alpha(scales.grey[100], 0.6)
					: "rgba(255, 255, 255, 0.09)",
		},
		portfolioArea:
			variant === "dark" ? DarkPortfolioAreaPalette : PortfolioAreaPalette,
		common: {
			black: color.Black,
			white: color.White,
		},
		warning: {
			light: scales.amber[100],
			main: scales.amber[600],
			dark: scales.amber[800],
		},
		grey: scales.grey,
		deprecated: {
			success: {
				main: color.Success,
				contrastText: color.White,
			},
			warning: {
				main: color.Warning,
				light: color.Warning25,
			},
			error: {
				main: color.Danger,
				light: color.Danger50,
				dark: color.Danger75,
				contrastText: color.White,
			},
			info: {
				main: color.Secondary,
				light: color.Secondary50,
				dark: color.Secondary50,
				contrastText: color.White,
			},
			grey: {
				50: color.MonoChrome50,
				100: color.MonoChrome100,
				200: color.MonoChrome200,
				300: color.MonoChrome300,
				400: color.MonoChrome400,
				500: color.MonoChrome500,
				600: color.MonoChrome600,
				700: color.MonoChrome700,
				800: color.MonoChrome800,
				900: color.Black,
			},
		},
		blue: {
			...scales.blue,
			light: scales.blue[100],
			main: scales.blue[600],
			dark: scales.blue[800],
			contrastText: color.White,
		},
		orange: {
			...scales.orange,
			light: scales.orange[100],
			main: scales.orange[600],
			dark: scales.orange[800],
			contrastText: color.White,
		},
		purple: {
			...scales.purple,
			light: scales.purple[100],
			main: scales.purple[600],
			dark: scales.purple[800],
			contrastText: color.White,
		},
		green: {
			...scales.green,
			light: scales.green[100],
			main: scales.green[600],
			dark: scales.green[800],
			contrastText: color.White,
		},
		amber: {
			...scales.amber,
			light: scales.amber[100],
			main: scales.amber[600],
			dark: scales.amber[800],
			contrastText: color.White,
		},
		red: {
			...scales.red,
			light: scales.red[100],
			main: scales.red[600],
			dark: scales.red[800],
			contrastText: color.White,
		},
		indigo: {
			...scales.indigo,
			light: scales.indigo[100],
			main: scales.indigo[600],
			dark: scales.indigo[800],
			contrastText: color.White,
		},
		teal: {
			...scales.teal,
			light: scales.teal[100],
			main: scales.teal[600],
			dark: scales.teal[800],
			contrastText: color.White,
		},
		yellow: {
			...scales.yellow,
			light: scales.yellow[100],
			main: scales.yellow[600],
			dark: scales.yellow[800],
			contrastText: color.White,
		},
		pink: {
			...scales.pink,
			light: scales.pink[100],
			main: scales.pink[600],
			dark: scales.pink[800],
			contrastText: color.White,
		},
		realization: {
			light: scales.green[50],
			main: variant === "light" ? scales.green[600] : "#7CC47F",
			dark: scales.green[100],
			contrastText: color.White,
		},
		myPLANN: {
			main: color.Secondary,
			main75: color.Secondary75,
			main50: color.Secondary50,
			main25: color.Secondary25,
			main10: color.Secondary10,
		},
		divider: surfaces.divider,
		darkmodeText: {
			white: variant === "light" ? color.White : color.Black,
			black: variant === "light" ? color.Black : color.White,
		},
	} as const satisfies PaletteOptions;
	const navigationConfig = {
		header: {
			height: 64,
		},
	} satisfies Theme["navigation"];
	const datePanelConfig = {
		cellHeight: 40,
		gridHeight: 280,
		columnGap: "6px",
		paddingBottom: "24px",
		weekdayLabel: {
			height: 28,
			fontSize: "11px",
		},
		disabledArrowOpacity: 0.4,
	} satisfies Theme["datePanel"];
	const activityConfig = {
		eventRow: {
			titleLines: {
				list: 2,
				preview: 1,
			},
			chevronOffset: 10,
		},
	} satisfies Theme["activity"];
	const notificationCenterConfig = {
		unreadDot: {
			size: 8,
			inset: 4,
		},
		unreadTintOpacity: 0.4,
		rowLineHeight: 1.5,
		emptyIconBoxSize: 56,
	} satisfies Theme["notificationCenter"];
	const inspirationConfig = {
		section: {
			cardGap: "12px",
			dragThreshold: 5,
			paddingBottom: 1.6,
			paddingLeft: 1.6,
			endSpacerWidth: 16,
			cursor: "grab",
		},
		card: {
			width: 160,
			height: 214,
			borderRadius: "16px",
			padding: 1.6,
			activeTransform: "scale(0.97)",
			overlayGradient:
				"linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0.1), transparent)",
			titleLineHeight: 1.25,
			textShadow: "0 1px 4px rgba(0,0,0,0.5)",
		},
		seeAll: {
			gap: 0.8,
			iconSize: 40,
			iconGlyphSize: 20,
			iconBorderRadius: "50%",
		},
		hero: {
			height: 310,
			overlayGradient:
				"linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.3), rgba(0,0,0,0.25))",
		},
	} satisfies Theme["inspiration"];
	const dashboardConfig = {
		gradients: {
			total: {
				from: variant === "dark" ? "#4A3A1C" : "#FF6B00",
				to: variant === "dark" ? "#30240F" : "#E65100",
			},
			investment: {
				from: variant === "dark" ? "#3B2A50" : "#9C27B0",
				to: variant === "dark" ? "#241936" : "#6A1B9A",
			},
			investmentProperty: {
				from: variant === "dark" ? "#244159" : "#2196F3",
				to: variant === "dark" ? "#15304A" : "#1565C0",
			},
			cash: {
				from: variant === "dark" ? "#214A32" : "#4CAF50",
				to: variant === "dark" ? "#123420" : "#2E7D32",
			},
			other: {
				from: variant === "dark" ? "#2D3A44" : "#37474F",
				to: variant === "dark" ? "#19242E" : "#263238",
			},
		},
		quickCalculator: {
			tints: {
				blue: {
					background: palette.blue[100],
					icon: palette.blue[600],
				},
				orange: {
					background: palette.orange[100],
					icon: palette.orange[600],
				},
				green: {
					background: palette.green[100],
					icon: palette.green[600],
				},
				purple: {
					background: palette.purple[100],
					icon: palette.purple[600],
				},
				rose: {
					background: variant === "dark" ? palette.red[100] : "#FFE4E6",
					icon: variant === "dark" ? palette.red[600] : "#E11D48",
				},
			},
		},
	} satisfies Theme["dashboard"];
	const chatConfig = {
		message: {
			maxWidth: "75%",
			gap: 0.2,
			paddingX: 1.2,
			paddingY: 0.8,
			lineHeight: 1.4,
			pendingOpacity: 0.6,
			marginBottom: {
				grouped: "2px",
				last: "8px",
			},
			borderRadius: {
				default: "16px",
				tight: "6px",
			},
		},
		avatar: {
			size: 28,
			gap: 0.6,
		},
		sender: {
			background: `linear-gradient(135deg, ${palette.blue[500]}, ${palette.blue[600]})`,
			attachmentBackground: palette.blue[600],
			textColor: palette.darkmodeText.white,
		},
		recipient: {
			background: palette.background.paper,
			textColor: palette.text.primary,
			elevation: 1,
		},
		timestamp: {
			inset: 0.4,
		},
	} satisfies Theme["chat"];
	const theme: ThemeOptions = {
		palette,
		breakpoints: {
			values: {
				xs: 0,
				sm: 600,
				md: 1050,
				lg: 1200,
				xl: 1536,
			},
		},
		spacing: 10,
		shape: {
			borderRadius: 4,
			pillRadius: PILL_RADIUS,
		},
		activity: activityConfig,
		notificationCenter: notificationCenterConfig,
		dashboard: dashboardConfig,
		inspiration: inspirationConfig,
		navigation: navigationConfig,
		datePanel: datePanelConfig,
		iconBoxSize,
		typography: {
			fontSize: 14,
			fontFamily: "'Inter', sans-serif",
			displayMd: {
				fontSize: "28px",
				fontWeight: 700,
				lineHeight: 1.15,
				letterSpacing: "-0.02em",
				color: palette.grey[900],
			},
			h1: {
				fontSize: "24px",
				fontWeight: 700,
				lineHeight: 1.2,
				color: palette.grey[900],
			},
			h2: {
				fontSize: "20px",
				fontWeight: 600,
				lineHeight: 1.25,
				color: palette.grey[900],
			},
			h3: {
				fontSize: "18px",
				fontWeight: 600,
				lineHeight: 1.3,
				color: palette.grey[900],
			},
			h4: {
				fontSize: "16px",
				fontWeight: 600,
				lineHeight: 1.3,
				color: palette.grey[900],
			},
			subtitle1: {
				fontSize: "16px",
				fontWeight: 400,
				lineHeight: 1.5,
				color: palette.grey[900],
			},
			subtitle2: {
				fontSize: "10px",
				fontWeight: 300,
				fontStyle: "italic",
				lineHeight: "14px",
				letterSpacing: "0.2px",
				color: palette.grey[500],
			},
			subtitle3: {
				fontSize: "16px",
				fontWeight: 700,
				color: palette.grey[900],
			},
			body1: {
				fontSize: "16px",
				fontWeight: 400,
				lineHeight: 1.5,
				color: palette.grey[900],
				bold: { fontWeight: 600 },
			},
			body2: {
				fontSize: "14px",
				fontWeight: 400,
				lineHeight: 1.45,
				color: palette.grey[500],
			},
			bodyStrong: {
				fontSize: "14px",
				fontWeight: 600,
				lineHeight: 1.45,
				color: palette.grey[900],
			},
			bodyMedium: {
				fontSize: "14px",
				fontWeight: 500,
				lineHeight: 1.45,
				color: palette.grey[900],
			},
			value: {
				fontSize: "14px",
				fontWeight: 700,
				lineHeight: 1.45,
				color: palette.grey[900],
			},
			button: {
				fontSize: 12,
				fontWeight: 500,
				lineHeight: 1.4,
				textTransform: "none",
			},
			overline: {
				fontSize: "12px",
				fontWeight: 600,
				lineHeight: 1.3,
				textTransform: "uppercase",
				letterSpacing: "0.05em",
				color: palette.grey[500],
			},
			caption: {
				fontSize: "12px",
				fontWeight: 400,
				lineHeight: 1.4,
				color: palette.grey[900],
			},
			hint: {
				fontSize: "12px",
				fontWeight: 400,
				lineHeight: 1.4,
				color: palette.grey[400],
			},
			deprecatedH1: {
				fontSize: 32,
				fontWeight: 400,
				lineHeight: "35px",
				color: color.Black,
			},
			deprecatedH2: {
				fontSize: 24,
				fontWeight: 400,
				lineHeight: "26px",
				letterSpacing: "0.2px",
				color: color.Black,
			},
			deprecatedH3: {
				fontSize: 16,
				fontWeight: 600,
				lineHeight: "17px",
				letterSpacing: "0.2px",
				color: color.Black,
			},
			deprecatedH4: {
				fontSize: 24,
				fontWeight: 700,
				color: color.Black,
			},
			deprecatedSubtitle1: {
				fontSize: 16,
				fontWeight: 400,
				lineHeight: 1.4,
				letterSpacing: "0.2px",
				color: color.Black,
			},
			deprecatedSubtitle2: {
				fontSize: 10,
				fontWeight: 300,
				fontStyle: "italic",
				lineHeight: "14px",
				letterSpacing: "0.2px",
				color: color.MonoChrome400,
			},
			deprecatedBody1: {
				fontSize: 14,
				fontWeight: 400,
				letterSpacing: "0.2px",
			},
			deprecatedBody2: {
				fontSize: 14,
				fontWeight: 600,
				letterSpacing: "0.2px",
				color: color.MonoChrome400,
			},
			deprecatedBodyStrong: {
				fontSize: "14px",
				fontWeight: 600,
				letterSpacing: "0.2px",
			},
			deprecatedBodyMedium: {
				fontSize: "14px",
				fontWeight: 500,
				letterSpacing: "0.2px",
			},
			deprecatedCaption: {
				fontSize: "12px",
				fontWeight: 400,
				letterSpacing: "0.2px",
				lineHeight: "15.6px",
			},
			deprecatedHint: {
				fontSize: "12px",
				fontWeight: 400,
				color: palette.deprecated.grey[400],
			},
		},
		components: {
			MuiBadge: {
				styleOverrides: {
					dot: {
						width: 12,
						height: 12,
						borderRadius: "50%",
						backgroundColor: palette.green[500],
						border: `2px solid ${palette.background.paper}`,
					},
				},
				variants: [
					{
						props: { variant: "headerGradient" },
						style: {
							"& .MuiBadge-badge": {
								right: 5,
								top: 5,
								minWidth: 16,
								height: 16,
								fontSize: 8,
								fontWeight: 700,
								padding: "0 4px",
								color: palette.darkmodeText.white,
								border: `2px solid ${palette.darkmodeText.white}`,
							},
						},
					},
					{
						props: { variant: "headerFlat" },
						style: ({ theme }) => ({
							"& .MuiBadge-badge": {
								right: 5,
								top: 5,
								color: theme.palette.getContrastText(
									theme.palette.deprecated.error.main,
								),
							},
						}),
					},
					{
						props: { variant: "button" },
						style: {
							"& .MuiBadge-badge": {
								right: 6,
								top: 4,
								color: palette.darkmodeText.white,
							},
						},
					},
				],
			},
			MuiDateCalendar: {
				styleOverrides: {
					root: {
						width: "100%",
						height: "auto",
						maxHeight: "none",
						margin: 0,
						overflow: "visible",
					},
				},
			},
			MuiDayCalendar: {
				styleOverrides: {
					header: {
						justifyContent: "space-between",
						paddingInline: "16px",
					},
					weekDayLabel: {
						fontSize: "12px",
						fontWeight: 400,
						lineHeight: 1.4,
						color: palette.grey[500],
						width: 40,
						height: 28,
						margin: "0 2px",
					},
					weekContainer: {
						justifyContent: "space-between",
						margin: "2px 0",
						paddingInline: "16px",
					},
					slideTransition: {
						minHeight: 264,
						marginBottom: "16px",
					},
				},
			},
			MuiPickersDay: {
				styleOverrides: {
					root: {
						fontSize: "14px",
						fontWeight: 500,
						lineHeight: 1.45,
						fontVariantNumeric: "tabular-nums",
						color: palette.grey[800],
						width: 40,
						height: 40,
						margin: "0 2px",
						borderRadius: "8px",
						"&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
							backgroundColor: palette.blue[600],
							color: palette.darkmodeText.white,
							fontWeight: 600,
						},
						"&.Mui-disabled:not(.Mui-selected)": {
							color: palette.grey[300],
						},
					},
				},
			},
			MuiMonthCalendar: {
				styleOverrides: {
					root: {
						width: "100%",
						paddingInline: "16px",
						paddingBottom: "16px",
						margin: 0,
						rowGap: "8px",
					},
					button: {
						fontSize: "14px",
						fontWeight: 500,
						lineHeight: 1.45,
						height: 40,
						color: palette.grey[800],
						borderRadius: "8px",
						"&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
							backgroundColor: palette.blue[600],
							color: palette.darkmodeText.white,
							fontWeight: 600,
						},
						"&.Mui-disabled": { color: palette.grey[300] },
					},
				},
			},
			MuiButtonBase: {
				defaultProps: {
					// The props to apply
					disableRipple: false, // let the ripple live again 💣!
				},
				styleOverrides: {
					root: {
						fontFamily: "inherit",
						"&.MuiButton-sizeLarge": {
							fontSize: "16px",
							fontWeight: 700,
							padding: "14px 91px",
							height: "auto",
						},
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					".chat_group": {
						"& > *": {
							marginBottom: chatConfig.message.marginBottom.grouped,
						},
						"& > :last-child": {
							marginBottom: chatConfig.message.marginBottom.last,
						},
						"& > :not(:last-child) .chat_group_tail": {
							display: "none",
						},
						"& .chat_bubble": {
							maxWidth: "100%",
							overflowWrap: "break-word",
							borderRadius: chatConfig.message.borderRadius.default,
							whiteSpace: "pre-wrap",
						},
						'&[data-message-direction="sender"]': {
							"& > :not(:first-child) .chat_bubble": {
								borderTopRightRadius: chatConfig.message.borderRadius.tight,
							},
							"& > :not(:last-child) .chat_bubble": {
								borderBottomRightRadius: chatConfig.message.borderRadius.tight,
							},
						},
						'&[data-message-direction="recipient"]': {
							"& > :not(:first-child) .chat_bubble": {
								borderTopLeftRadius: chatConfig.message.borderRadius.tight,
							},
							"& > :not(:last-child) .chat_bubble": {
								borderBottomLeftRadius: chatConfig.message.borderRadius.tight,
							},
						},
					},
					"*": {
						WebkitOverflowScrolling: "touch",
					},
					"@supports (scrollbar-width: thin)": {
						"*": {
							scrollbarWidth: "thin",
							scrollbarColor: `${SCROLLBAR_THUMB_COLOR} transparent`,
						},
					},
					"*::-webkit-scrollbar": {
						display: "block",
						width: `${SCROLLBAR_SIZE_PX}px`,
						height: `${SCROLLBAR_SIZE_PX}px`,
					},
					"*::-webkit-scrollbar-track": {
						width: `${SCROLLBAR_SIZE_PX}px`,
						backgroundColor: "transparent",
					},
					"*::-webkit-scrollbar-thumb": {
						backgroundColor: SCROLLBAR_THUMB_COLOR,
						borderRadius: `${SCROLLBAR_RADIUS_PX}px`,
						"&:hover": {
							backgroundColor: SCROLLBAR_THUMB_COLOR_HOVER,
						},
					},
					html: {
						height: "100%",
						position: "relative",
						backgroundColor: color.White,
						overscrollBehavior: "none",
					},
					body: {
						backgroundColor: color.White,
						height: "100%",
						overflow: "hidden",
						position: "relative",
						color: color.Black,
						overscrollBehavior: "none",
						fontWeight: 400,
					},
					root: {
						overscrollBehavior: "none",
					},
				},
			},
			MuiLink: {
				styleOverrides: {
					root: {
						fontSize: "12px",
						color: color.Secondary,
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						boxShadow: "0 2px 6px 0 rgba(215, 215, 215, 0.5)",
						"& .MuiPickersBasePicker-container .MuiPickerDTTabs-tabs .MuiButtonBase-root":
							{
								color: color.White,
							},
					},
				},
			},
			MuiAlert: {
				variants: (
					[
						["info", palette.blue],
						["success", palette.green],
						["warning", palette.amber],
						["error", palette.red],
					] as const
				).map(([severity, tint]) => ({
					props: { variant: "notice", severity },
					style: {
						alignItems: "flex-start",
						padding: "12px 14px",
						backgroundColor: tint[50],
						border: solidBorder(tint[200]),
						borderRadius: "12px",
						boxShadow: "none",
						"& .MuiAlert-icon": {
							marginRight: "10px",
							marginTop: "1px",
							padding: 0,
							opacity: 1,
						},
						"& .MuiAlert-message": {
							padding: 0,
							minWidth: 0,
							fontSize: "12px",
							fontWeight: 400,
							lineHeight: 1.5,
							color: palette.grey[600],
						},
						"& .MuiAlertTitle-root": {
							margin: "0 0 2px",
							fontSize: "14px",
							fontWeight: 600,
							lineHeight: 1.375,
							color: tint[800],
						},
					},
				})),
			},
			MuiButton: {
				defaultProps: {
					variant: "contained",
					loadingIndicator: createElement(CircularProgress, {
						size: 20,
						sx: { color: "inherit" },
					}),
				},
				styleOverrides: {
					root: {
						textTransform: "none",
						borderRadius: "28px",
						fontSize: "16px",
						fontWeight: "700",
						boxShadow: "none",
						"&:hover": {
							boxShadow: "none",
						},
						"&.Mui-disabled": {
							backgroundColor:
								variant === "dark"
									? palette.deprecated.grey[500]
									: palette.deprecated.grey[300],
						},
						"&.MuiButton-loading, &.MuiButton-loading.Mui-disabled": {
							color: "transparent",
						},
					},
					sizeMedium: {
						height: "56px",
					},
					text: {
						border: "none",
						"&:hover": {
							boxShadow: "none",
							border: "none",
						},
					},
					contained: {
						color:
							variant === "dark" ? palette.common.black : palette.common.white,
					},
				},
				variants: [
					{
						props: { variant: "alertCta" },
						style: ({ theme }) => ({
							...theme.typography.bodyStrong,
							color: palette.common.white,
							borderRadius: "12px",
							paddingTop: theme.spacing(1),
							paddingBottom: theme.spacing(1),
							textTransform: "none",
							"& .MuiButton-startIcon": {
								marginRight: theme.spacing(0.6),
								marginLeft: 0,
							},
							"&:active": { opacity: 0.8, transform: "scale(0.98)" },
						}),
					},
					{
						props: { variant: "alertCta", color: "warning" },
						style: {
							backgroundColor: palette.amber[500],
							"&:hover": { backgroundColor: palette.amber[500] },
						},
					},
					{
						props: { variant: "alertCta", color: "info" },
						style: {
							backgroundColor: palette.blue[600],
							"&:hover": { backgroundColor: palette.blue[600] },
						},
					},
					{
						props: { variant: "alertCta", color: "orange" },
						style: {
							backgroundColor: palette.orange[500],
							"&:hover": { backgroundColor: palette.orange[500] },
						},
					},
					{
						props: { variant: "alertCta", color: "neutral" },
						style: {
							backgroundColor: palette.grey[600],
							"&:hover": { backgroundColor: palette.grey[600] },
						},
					},
					{
						// Secondary (text-only) action of an Event Alert box.
						props: { variant: "alertCtaText" },
						style: ({ theme }) => ({
							...theme.typography.bodyMedium,
							color: palette.text.secondary,
							paddingTop: theme.spacing(theme.spacingTokens.sm),
							paddingBottom: theme.spacing(theme.spacingTokens.sm),
							textTransform: "none",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { opacity: 0.7 },
						}),
					},
					{
						props: { variant: "tile" },
						style: {
							width: "100%",
							justifyContent: "flex-start",
							textAlign: "left",
							gap: 10,
							padding: "12px",
							borderRadius: "12px",
							border: `1px solid ${palette.grey[200]}`,
							backgroundColor: "transparent",
							color: palette.grey[900],
							fontWeight: 500,
							fontSize: "14px",
							lineHeight: 1.25,
							textTransform: "none",
							height: "auto",
							boxShadow: "none",
							minWidth: 0,
							"& .MuiButton-startIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
							"&:hover": {
								backgroundColor: "transparent",
								borderColor: palette.grey[300],
								boxShadow: "none",
							},
							"&:active": {
								backgroundColor: palette.grey[50],
							},
						},
					},
					{
						props: { variant: "inlineAction" },
						style: {
							width: "100%",
							height: "auto",
							minHeight: 48,
							justifyContent: "flex-start",
							paddingLeft: "16px",
							paddingRight: "16px",
							gap: "8px",
							borderRadius: 0,
							color: palette.blue.main,
							fontSize: "14px",
							fontWeight: 500,
							textTransform: "none",
							transition: "opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { opacity: 0.7, backgroundColor: "transparent" },
							"&.Mui-disabled": { backgroundColor: "transparent" },
						},
					},
					{
						props: { variant: "disclosure" },
						style: {
							width: "100%",
							height: "auto",
							minHeight: 40,
							justifyContent: "flex-start",
							paddingLeft: "16px",
							paddingRight: "16px",
							gap: "4px",
							borderRadius: 0,
							color: palette.blue.main,
							fontSize: "14px",
							fontWeight: 500,
							textTransform: "none",
							"& .MuiButton-endIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { backgroundColor: palette.blue[50] },
							'&[aria-expanded="true"]': {
								borderTop: solidBorder(palette.divider),
							},
						},
					},
					{
						// Inline row-level CTA that closes a list section (design system:
						// icon left, blue label, full-bleed, no border of its own — the
						// enclosing section supplies the hairlines).
						props: { variant: "listCta" },
						style: {
							width: "100%",
							height: "auto",
							minHeight: 48,
							justifyContent: "flex-start",
							textAlign: "left",
							padding: "0 16px",
							gap: "8px",
							borderRadius: 0,
							color: palette.blue[600],
							fontSize: "14px",
							fontWeight: 500,
							lineHeight: 1.45,
							textTransform: "none",
							background: "transparent",
							boxShadow: "none",
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"& .MuiButton-startIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { backgroundColor: palette.grey[50] },
							"&.Mui-disabled": { backgroundColor: "transparent" },
						},
					},
					{
						props: { variant: "showAll" },
						style: {
							width: "100%",
							height: "auto",
							minHeight: 0,
							justifyContent: "flex-start",
							textAlign: "left",
							padding: "8px 16px",
							borderRadius: 0,
							borderTop: solidBorder(palette.surface.border),
							color: palette.blue.main,
							fontSize: "14px",
							fontWeight: 500,
							lineHeight: 1.45,
							textTransform: "none",
							background: "transparent",
							transition: "opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { backgroundColor: palette.blue[50] },
							"&.Mui-disabled": { backgroundColor: "transparent" },
							"& .MuiButton-endIcon": {
								marginLeft: "4px",
								marginRight: 0,
							},
						},
					},
					{
						props: { variant: "addRow" },
						style: {
							width: "100%",
							height: "auto",
							minHeight: 48,
							justifyContent: "flex-start",
							textAlign: "left",
							padding: "0 16px",
							gap: "8px",
							borderRadius: 0,
							borderTop: solidBorder(palette.surface.border),
							color: palette.blue.main,
							fontSize: "14px",
							fontWeight: 500,
							lineHeight: 1.45,
							textTransform: "none",
							background: "transparent",
							"& .MuiButton-startIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { backgroundColor: palette.grey[50] },
							"&.Mui-disabled": { backgroundColor: "transparent" },
						},
					},
					{
						props: { variant: "deprecatedListRow" },
						style: {
							width: "100%",
							height: "auto",
							minHeight: 0,
							justifyContent: "flex-start",
							paddingTop: "14px",
							paddingBottom: "14px",
							paddingLeft: "20px",
							paddingRight: "20px",
							gap: "14px",
							borderRadius: 0,
							color: "inherit",
							fontSize: "inherit",
							fontWeight: 400,
							textTransform: "none",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { backgroundColor: palette.deprecated.grey[50] },
							"&.Mui-disabled": { backgroundColor: "transparent" },
						},
					},
					{
						props: { variant: "deprecatedHeaderAction" },
						style: {
							paddingInline: "8px",
							color: palette.primary.main,
							"& .MuiButton-startIcon": { marginRight: "5px" },
						},
					},
					{
						props: { variant: "deprecatedHeaderAction", color: "neutral" },
						style: { color: palette.grey[600] },
					},
					{
						props: { variant: "primary" },
						style: {
							height: "auto",
							minHeight: 48,
							width: "100%",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
							padding: "14px",
							borderRadius: "16px",
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 600,
							color: palette.darkmodeText.white,
							backgroundColor: palette.blue[600],
							boxShadow: `0 4px 14px ${alpha(palette.blue[500], 0.32)}`,
							transition:
								"transform 200ms cubic-bezier(0.16, 1, 0.3, 1), background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"& .MuiButton-startIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
							"&:hover": {
								backgroundColor: palette.blue[600],
								boxShadow: `0 4px 14px ${alpha(palette.blue[500], 0.32)}`,
							},
							"&:active": { transform: "scale(0.97)" },
							"&.Mui-disabled": {
								backgroundColor: palette.grey[100],
								color: palette.grey[400],
								boxShadow: "none",
							},
						},
					},
					{
						props: { variant: "primary", color: "success" },
						style: {
							backgroundColor: palette.green[600],
							boxShadow: `0 6px 16px -4px ${alpha(palette.green[600], 0.4)}`,
							"&:hover": {
								backgroundColor: palette.green[600],
								boxShadow: `0 6px 16px -4px ${alpha(palette.green[600], 0.4)}`,
							},
						},
					},
					{
						props: { variant: "primary", color: "warning" },
						style: {
							backgroundColor: palette.amber[600],
							boxShadow: `0 6px 16px -4px ${alpha(palette.amber[600], 0.4)}`,
							"&:hover": {
								backgroundColor: palette.amber[600],
								boxShadow: `0 6px 16px -4px ${alpha(palette.amber[600], 0.4)}`,
							},
						},
					},
					{
						props: { variant: "primary", color: "error" },
						style: {
							backgroundColor: palette.red[600],
							boxShadow: "none",
							"&:hover": { backgroundColor: palette.red[600] },
						},
					},
					{
						props: { variant: "primary", size: "small" },
						style: ({ theme }) => ({
							width: "auto",
							minHeight: theme.spacing(theme.spacingTokens.xxl),
							padding: theme.spacing(
								theme.spacingTokens.xs,
								theme.spacingTokens.md,
							),
						}),
					},
					{
						props: { variant: "secondary" },
						style: {
							height: "auto",
							width: "100%",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
							padding: "14px",
							borderRadius: "16px",
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 500,
							color: palette.grey[700],
							backgroundColor: palette.background.paper,
							border: solidBorder(palette.grey[200]),
							boxShadow: "none",
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: palette.background.paper },
							"&:active": { backgroundColor: palette.grey[50] },
							"&.Mui-disabled": { color: palette.grey[400] },
						},
					},
					{
						props: { variant: "secondary", color: "success" },
						style: {
							color: palette.green[700],
							borderColor: palette.green[200],
							"&:active": { backgroundColor: palette.green[50] },
						},
					},
					{
						props: { variant: "secondary", color: "warning" },
						style: {
							color: palette.amber[700],
							borderColor: palette.amber[200],
							"&:active": { backgroundColor: palette.amber[50] },
						},
					},
					{
						props: { variant: "secondary", color: "error" },
						style: {
							color: palette.red[600],
							borderColor: palette.red[200],
							"&:active": { backgroundColor: palette.red[50] },
						},
					},
					{
						props: { variant: "tertiary" },
						style: {
							height: "auto",
							width: "100%",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
							padding: "14px",
							borderRadius: "16px",
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 500,
							color: palette.grey[700],
							backgroundColor: palette.grey[100],
							boxShadow: "none",
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"& .MuiButton-startIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
							"&:hover": { backgroundColor: palette.grey[100] },
							"&:active": { backgroundColor: palette.grey[200] },
							"&.Mui-disabled": { color: palette.grey[400] },
						},
					},
					{
						props: { variant: "tertiary", color: "success" },
						style: {
							color: palette.green[700],
							backgroundColor: palette.green[100],
							"&:hover": { backgroundColor: palette.green[100] },
							"&:active": { backgroundColor: palette.green[200] },
						},
					},
					{
						props: { variant: "tertiary", color: "warning" },
						style: {
							color: palette.amber[700],
							backgroundColor: palette.amber[100],
							"&:hover": { backgroundColor: palette.amber[100] },
							"&:active": { backgroundColor: palette.amber[200] },
						},
					},
					{
						props: { variant: "tertiary", color: "error" },
						style: {
							color: palette.red[600],
							backgroundColor: palette.red[50],
							"&:hover": { backgroundColor: palette.red[50] },
							"&:active": { backgroundColor: palette.red[100] },
						},
					},
					{
						props: { variant: "ghost" },
						style: {
							height: "auto",
							width: "100%",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
							padding: "14px",
							borderRadius: "16px",
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 500,
							color: palette.grey[700],
							backgroundColor: "transparent",
							boxShadow: "none",
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { backgroundColor: palette.grey[50] },
							"&.Mui-disabled": { color: palette.grey[400] },
						},
					},
					{
						props: { variant: "ghost", color: "success" },
						style: {
							color: palette.green[700],
							"&:active": { backgroundColor: palette.green[50] },
						},
					},
					{
						props: { variant: "ghost", color: "warning" },
						style: {
							color: palette.amber[700],
							"&:active": { backgroundColor: palette.amber[50] },
						},
					},
					{
						props: { variant: "ghost", color: "error" },
						style: {
							color: palette.red[600],
							"&:active": { opacity: 0.7, backgroundColor: "transparent" },
						},
					},
					{
						props: { variant: "textLink" },
						style: {
							display: "inline-flex",
							alignItems: "center",
							gap: "6px",
							padding: 0,
							minWidth: 0,
							height: "auto",
							minHeight: 0,
							borderRadius: 0,
							backgroundColor: "transparent",
							fontSize: "14px",
							fontWeight: 600,
							textTransform: "none",
							color: palette.blue[600],
							boxShadow: "none",
							transition: "opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { opacity: 0.7, backgroundColor: "transparent" },
							"&.Mui-disabled": {
								color: palette.grey[400],
								backgroundColor: "transparent",
							},
						},
					},
					{
						props: { variant: "textLink", color: "error" },
						style: { color: palette.red[500] },
					},
					{
						props: { variant: "textLink", color: "neutral" },
						style: { color: palette.grey[600] },
					},
					{
						props: { variant: "textLink", size: "small" },
						style: ({ theme }) => ({
							fontSize: "12px",
							fontWeight: 500,
							lineHeight: 1.4,
							paddingBlock: theme.spacing(0.4),
							gap: theme.spacing(0.4),
							"& .MuiButton-endIcon": {
								marginLeft: 0,
								marginRight: 0,
							},
						}),
					},
				],
			},
			MuiAvatar: {
				variants: [
					{
						props: { variant: "softTeal" },
						style: {
							...softAvatarBase,
							backgroundColor: palette.teal[100],
							color: palette.teal[600],
						},
					},
					{
						props: { variant: "softGreen" },
						style: {
							...softAvatarBase,
							backgroundColor: palette.green[100],
							color: palette.green[600],
						},
					},
					{
						props: { variant: "softOrange" },
						style: {
							...softAvatarBase,
							backgroundColor: palette.orange[100],
							color: palette.orange[600],
						},
					},
					{
						props: { variant: "softBlue" },
						style: {
							...softAvatarBase,
							backgroundColor: palette.blue[100],
							color: palette.blue[600],
						},
					},
					{
						props: { variant: "softPurple" },
						style: {
							...softAvatarBase,
							backgroundColor: palette.purple[100],
							color: palette.purple[600],
						},
					},
					{
						props: { variant: "softGrey" },
						style: {
							...softAvatarBase,
							backgroundColor: palette.grey[100],
							color: palette.grey[600],
						},
					},
				],
			},
			MuiChip: {
				variants: [
					{
						props: { variant: "pill" },
						style: {
							height: "auto",
							borderRadius: 999,
							border: solidBorder(palette.grey[200]),
							backgroundColor: palette.surface.muted,
							fontSize: "12px",
							fontWeight: 400,
							lineHeight: 1.4,
							color: palette.grey[400],
							"& .MuiChip-label": { padding: "2px 12px" },
						},
					},
					{
						props: { variant: "pill", color: "info" },
						style: {
							backgroundColor: palette.blue[50],
							borderColor: palette.blue[200],
							color: palette.blue[700],
						},
					},
					{
						props: { variant: "pill", color: "success" },
						style: {
							backgroundColor: palette.green[50],
							borderColor: palette.green[200],
							color: palette.green[700],
						},
					},
					{
						props: { variant: "pill", color: "warning" },
						style: {
							backgroundColor: palette.amber[50],
							borderColor: palette.amber[200],
							color: palette.amber[700],
						},
					},
					{
						props: { variant: "pill", color: "error" },
						style: {
							backgroundColor: palette.red[50],
							borderColor: palette.red[200],
							color: palette.red[700],
						},
					},
					{
						props: { variant: "pill", color: "orange" },
						style: {
							backgroundColor: palette.orange[50],
							borderColor: palette.orange[200],
							color: palette.orange[700],
						},
					},
					{
						props: { variant: "tag" },
						style: {
							height: "auto",
							borderRadius: 999,
							fontSize: "12px",
							fontWeight: 500,
							backgroundColor: palette.grey[100],
							color: palette.grey[600],
							"& .MuiChip-label": { padding: "2px 6px" },
							"& .MuiChip-icon": {
								marginLeft: "8px",
								marginRight: "-2px",
								color: "inherit",
							},
						},
					},
					{
						props: { variant: "tag", color: "info" },
						style: {
							backgroundColor: palette.blue[50],
							color: palette.blue[700],
						},
					},
					{
						props: { variant: "tag", color: "success" },
						style: {
							backgroundColor: palette.green[50],
							color: palette.green[700],
						},
					},
					{
						props: { variant: "tag", color: "warning" },
						style: {
							backgroundColor: palette.amber[50],
							color: palette.amber[700],
						},
					},
					{
						props: { variant: "tag", color: "error" },
						style: {
							backgroundColor: palette.red[50],
							color: palette.red[700],
						},
					},
					{
						props: { variant: "filter" },
						style: {
							height: "auto",
							borderRadius: 999,
							fontSize: "12px",
							fontWeight: 500,
							cursor: "pointer",
							backgroundColor: palette.background.paper,
							color: palette.grey[500],
							border: `1px solid ${palette.grey[200]}`,
							"& .MuiChip-label": { padding: "6px 12px" },
							"& .MuiChip-icon": {
								marginLeft: "12px",
								marginRight: "-6px",
								color: "inherit",
							},
						},
					},
					{
						props: { variant: "filter", color: "warning" },
						style: {
							backgroundColor: palette.amber[50],
							color: palette.amber[600],
							borderColor: palette.amber[200],
						},
					},
					{
						props: { variant: "filter", color: "info" },
						style: {
							backgroundColor: palette.blue[50],
							color: palette.blue[600],
							borderColor: palette.blue[200],
						},
					},
					{
						props: { variant: "trend" },
						style: {
							height: "auto",
							borderRadius: "8px",
							fontSize: "14px",
							fontWeight: 700,
							flexShrink: 0,
							fontVariantNumeric: "tabular-nums",
							"& .MuiChip-label": { padding: "6px 10px 6px 4px" },
							"& .MuiChip-icon": {
								color: "inherit",
								marginLeft: "10px",
								marginRight: "0",
							},
						},
					},
					{
						props: { variant: "trend", color: "success" },
						style: {
							backgroundColor: palette.green[100],
							color: palette.green[600],
						},
					},
					{
						props: { variant: "trend", color: "error" },
						style: {
							backgroundColor: palette.red[100],
							color: palette.red[600],
						},
					},
					{
						props: { variant: "performance" },
						style: {
							height: "auto",
							borderRadius: 999,
							fontSize: "12px",
							fontWeight: 500,
							"& .MuiChip-label": {
								padding: "2px 6px",
								lineHeight: 1.25,
							},
							"& .MuiChip-icon": {
								color: "inherit",
								marginLeft: "6px",
								marginRight: "-2px",
							},
						},
					},
					{
						props: { variant: "performance", color: "success" },
						style: {
							backgroundColor: palette.green[100],
							color: palette.green[700],
						},
					},
					{
						props: { variant: "performance", color: "error" },
						style: {
							backgroundColor: palette.red[100],
							color: palette.red[700],
						},
					},
				],
			},
			MuiIconButton: {
				variants: [
					{
						props: { variant: "solid" },
						style: {
							width: 32,
							height: 32,
							borderRadius: PILL_RADIUS,
							color: palette.grey[700],
							backgroundColor: palette.grey[100],
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: palette.grey[100] },
							"&:active": { backgroundColor: palette.grey[200] },
							"&.Mui-disabled": {
								backgroundColor: palette.grey[50],
								color: palette.grey[300],
							},
						},
					},
					{
						props: { variant: "solid", color: "error" },
						style: {
							color: palette.darkmodeText.white,
							backgroundColor: palette.red[500],
							"&:hover": { backgroundColor: palette.red[500] },
							"&:active": { transform: "scale(0.95)" },
						},
					},
					{
						props: { variant: "soft" },
						style: {
							width: 32,
							height: 32,
							borderRadius: PILL_RADIUS,
							color: palette.blue[600],
							backgroundColor: palette.blue[50],
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: palette.blue[50] },
							"&:active": { backgroundColor: palette.blue[100] },
							"&.Mui-disabled": {
								backgroundColor: palette.grey[50],
								color: palette.grey[300],
							},
						},
					},
					{
						props: { variant: "soft", color: "error" },
						style: {
							color: palette.red[600],
							backgroundColor: palette.red[50],
							"&:hover": { backgroundColor: palette.red[50] },
							"&:active": { backgroundColor: palette.red[100] },
						},
					},
					{
						props: { variant: "ghost" },
						style: {
							width: 32,
							height: 32,
							borderRadius: PILL_RADIUS,
							color: palette.grey[700],
							backgroundColor: "transparent",
							transition:
								"background-color 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": {
								backgroundColor: alpha(palette.common.black, 0.05),
							},
							"&.Mui-disabled": { color: palette.grey[300] },
						},
					},
					{
						props: { variant: "ghost", color: "error" },
						style: {
							color: palette.red[500],
							"&:active": { backgroundColor: palette.red[50] },
						},
					},
					{
						props: { variant: "header" },
						style: {
							width: 40,
							height: 40,
							padding: 0,
							borderRadius: "9999px",
							color: palette.grey[700],
							backgroundColor: "transparent",
							transition: "transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: "transparent" },
							"&:active": { transform: "scale(0.95)" },
							"&.Mui-disabled": { color: palette.grey[300] },
						},
					},
					{
						props: { variant: "header", color: "error" },
						style: {
							color: palette.red[500],
							"&:active": { backgroundColor: palette.red[50] },
						},
					},
					{
						props: { variant: "fab" },
						style: {
							width: 32,
							height: 32,
							borderRadius: PILL_RADIUS,
							color: palette.darkmodeText.white,
							backgroundColor: palette.blue[600],
							boxShadow: `0 4px 14px ${alpha(palette.blue[500], 0.32)}`,
							transition: "transform 200ms cubic-bezier(0.16, 1, 0.3, 1)",
							"&:hover": { backgroundColor: palette.blue[600] },
							"&:active": { transform: "scale(0.95)" },
						},
					},
					{
						props: { variant: "fab", color: "error" },
						style: {
							backgroundColor: palette.red[500],
							"&:hover": { backgroundColor: palette.red[500] },
						},
					},
					{
						props: { size: "large" },
						style: { width: 40, height: 40 },
					},
				],
			},
			MuiTypography: {
				variants: [
					{
						props: { tabular: true },
						style: { fontVariantNumeric: "tabular-nums" },
					},
				],
			},
			MuiSlider: {
				variants: [
					{
						props: { variant: "flat" },
						style: {
							paddingTop: "4px",
							paddingBottom: "4px",
							"& .MuiSlider-rail": {
								height: 8,
								borderRadius: "999px",
								backgroundColor: palette.deprecated.grey[200],
								opacity: 1,
							},
							"& .MuiSlider-track": {
								height: 8,
								borderRadius: "999px",
								backgroundColor: "transparent",
								border: "none",
							},
							"& .MuiSlider-thumb": {
								width: 16,
								height: 16,
								backgroundColor: palette.blue.main,
								"&:hover, &.Mui-focusVisible, &.Mui-active": {
									boxShadow: "none",
								},
							},
						},
					},
				],
			},
			MuiLinearProgress: {
				variants: [
					{
						props: { slim: true },
						style: {
							height: 6,
							borderRadius: 3,
							backgroundColor: palette.deprecated.grey[100],
							"& .MuiLinearProgress-bar": {
								borderRadius: 3,
							},
						},
					},
				],
			},
			MuiDivider: {
				styleOverrides: {
					root: ({ ownerState }) => ({
						"&::before, &::after": {
							borderColor: color.MonoChrome100,
						},
						...(ownerState.redesigned && {
							borderColor: palette.grey[100],
						}),
					}),
				},
			},
			MuiInputBase: {
				variants: [
					{
						props: { variant: "pill" },
						style: {
							paddingInline: 12,
							paddingBlock: 8,
							fontSize: 14,
							backgroundColor: palette.grey[100],
							borderRadius: PILL_RADIUS,
							"& .MuiInputBase-input": {
								padding: 0,
								"&::placeholder": {
									color: palette.grey[400],
									opacity: 1,
								},
							},
						},
					},
				],
			},
			MuiInputLabel: {
				styleOverrides: {
					root: {
						lineHeight: "1.9em",
					},
					shrink: {
						pointerEvents: "none",
						fontWeight: 400,
						fontSize: "12px",
						color: color.MonoChrome400,
						padding: 0,
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						fontWeight: 500,
						fontSize: "14px",
						padding: "8px 12px",
						border: solidBorder(palette.deprecated.grey[300]),
						borderRadius: "12px",

						"& .MuiOutlinedInput-notchedOutline": {
							border: "none",
						},
						":not(MuiSvgIcon-root)+svg:first-of-type": {
							order: 0,
						},
						"*:nth-last-child(2)": {
							":not(.MuiChip-label):not(.MuiChip-root)": {
								order: 3,
							},
						},
					},
					input: {
						padding: 0,
					},
					notchedOutline: {},
				},
			},
			MuiMenu: {
				styleOverrides: {
					list: {
						backgroundColor: color.MonoChrome50,
					},
				},
				variants: [
					{
						props: { redesign: true },
						style: {
							// TODO: Replace the `.MuiPaper-root` / `.MuiMenu-list`
							// targeting with plain `paper` / `list` slot overrides once
							// the legacy `MuiPopover` and `MuiMenu` overrides above are
							// gone. They paint deprecated `MonoChrome` tokens over every
							// popover paper (slate background, light border, no shadow)
							// from the Popover root, so a redesigned menu can only win by
							// matching that specificity.
							"& .MuiPaper-root": {
								width: 240,
								borderRadius: "16px",
								border: solidBorder(palette.grey[200]),
								boxShadow:
									"0 20px 25px rgba(0, 0, 0, 0.08), 0 8px 10px rgba(0, 0, 0, 0.04)",
								backgroundColor: palette.background.paper,
								backgroundImage: "none",
							},
							"& .MuiMenu-list": {
								padding: 0,
								backgroundColor: "transparent",
							},
							"& .MuiMenuItem-root": {
								gap: "12px",
								padding: "12px 16px",
								backgroundColor: "transparent",
							},
							// MUI's `MenuItem` sets `min-width: 36px` on the icon slot
							// through a descendant selector, so it has to be matched
							// here to keep the icon flush with the 12px gap.
							"& .MuiMenuItem-root .MuiListItemIcon-root": {
								minWidth: 0,
								color: palette.grey[500],
							},
							"& .MuiDivider-root, & .MuiMenuItem-root + .MuiDivider-root": {
								margin: 0,
								borderColor: palette.grey[100],
							},
						},
					},
				],
			},
			MuiMenuItem: {
				styleOverrides: {
					root: {
						background: color.MonoChrome50,
						hover: {
							background: color.MonoChrome300,
						},
					},
				},
			},
			MuiListItemText: {
				defaultProps: {
					slotProps: { primary: { variant: "bodyMedium" } },
				},
			},
			MuiTabs: {
				styleOverrides: {
					root: {
						width: "95%",
						margin: "0 auto",
						borderBottom: solidBorder(color.MonoChrome100),
					},
				},
				variants: [
					{
						props: { redesigned: true },
						style: {
							width: "100%",
							margin: 0,
							minHeight: 0,
							padding: "4px",
							borderRadius: "12px",
							borderBottom: "none",
							backgroundColor: palette.grey[100],
							"& .MuiTabs-indicator": { display: "none" },
							"& .MuiTabs-flexContainer": { gap: "4px" },
						},
					},
					{
						props: { deprecatedUnderline: true },
						style: {
							width: "100%",
							minHeight: 44,
							backgroundColor: palette.common.white,
							"& .MuiTab-root": {
								minHeight: 44,
								paddingInline: "20px",
								fontSize: "14px",
								fontWeight: 500,
								textTransform: "none",
								color: palette.grey[500],
								"&.Mui-selected": {
									color: palette.secondary.main,
									fontWeight: 600,
								},
							},
						},
					},
				],
			},
			MuiTab: {
				styleOverrides: {
					root: {
						flexGrow: 1,
						textAlign: "center",
						color: color.Black,
						"&.Mui-selected": {
							color: color.Black,
							fontWeight: 700,
						},
					},
				},
				variants: [
					{
						props: { redesigned: true },
						style: {
							flex: 1,
							maxWidth: "none",
							minWidth: 0,
							minHeight: 0,
							height: 33,
							padding: 0,
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 500,
							borderRadius: "8px",
							border: "1px solid transparent",
							color: palette.grey[500],
							transition: "background-color 150ms, box-shadow 150ms",
							"&.Mui-selected": {
								fontWeight: 500,
								color: palette.grey[900],
								backgroundColor: palette.background.paper,
								// Prototype `--c-elevated-border`: invisible in light mode
								// (elevation from the shadow alone), faint hairline in dark.
								borderColor:
									palette.mode === "dark"
										? "rgba(255, 255, 255, 0.1)"
										: "transparent",
								boxShadow: elevatedShadow,
							},
						},
					},
				],
			},
			MuiToggleButtonGroup: {
				variants: [
					{
						props: { segmented: true },
						style: {
							width: "100%",
							display: "flex",
							gap: "4px",
							padding: "4px",
							borderRadius: "12px",
							backgroundColor: palette.background.paper,
							"& .MuiToggleButtonGroup-grouped": {
								margin: 0,
								borderRadius: "8px",
							},
						},
					},
				],
			},
			MuiToggleButton: {
				variants: [
					{
						props: { segmented: true },
						style: {
							flex: 1,
							gap: "8px",
							padding: "8px",
							border: "none",
							borderRadius: "8px",
							textTransform: "none",
							fontSize: "14px",
							fontWeight: 500,
							color: palette.grey[500],
							transition: "background-color 150ms, box-shadow 150ms",
							"&.Mui-selected, &.Mui-selected:hover": {
								color: palette.darkmodeText.white,
								backgroundColor: palette.blue[600],
								boxShadow: elevatedShadow,
							},
						},
					},
				],
			},
			MuiSwitch: {
				defaultProps: {
					disableRipple: true,
				},
				styleOverrides: {
					root: ({ ownerState }) => ({
						width: ownerState.redesigned ? 44 : 48,
						height: 26,
						padding: 0,
						"& .MuiSwitch-switchBase": {
							padding: 0,
							margin: ownerState.redesigned ? 3 : 2,
							transitionDuration: "300ms",
							"&.Mui-checked": {
								// translate = width − thumb − 2·margin
								transform: ownerState.redesigned
									? "translateX(18px)"
									: "translateX(22px)",
								"& + .MuiSwitch-track": {
									backgroundColor: ownerState.redesigned
										? palette.blue[600]
										: palette.primary.main,
									opacity: 1,
									border: 0,
								},
								"&.Mui-disabled + .MuiSwitch-track": {
									opacity: 0.5,
								},
							},
							"&.Mui-disabled .MuiSwitch-thumb": {
								color: palette.primary.main,
							},
							"&.Mui-disabled + .MuiSwitch-track": {
								opacity: 1,
							},
						},
						"& .MuiSwitch-thumb": {
							boxSizing: "border-box",
							width: ownerState.redesigned ? 20 : 22,
							height: ownerState.redesigned ? 20 : 22,
							backgroundColor: palette.darkmodeText.white,
						},
						"& .MuiSwitch-track": {
							borderRadius: 26 / 2,
							backgroundColor: ownerState.redesigned
								? palette.grey[200]
								: color.MonoChrome300,
							opacity: 1,
						},
					}),
				},
			},
			MuiRadio: {
				styleOverrides: {},
			},
			MuiFormControlLabel: {
				styleOverrides: {
					root: {
						paddingInline: "16px",
						margin: "0px",
						flexDirection: "row-reverse",
						width: "100%",
						justifyContent: "space-between",
						borderBottom: "none",
						"&:not(:last-of-type)": {
							borderBottom: solidBorder(color.MonoChrome100),
						},
						"&:last-of-type": {
							paddingBottom: "1px",
						},
					},
					label: {
						fontSize: "15px",
						fontWeight: 400,
						paddingTop: "12px",
						paddingBottom: "12px",
					},
				},
			},
			MuiCheckbox: {
				defaultProps: {
					size: "small",
				},
				styleOverrides: {
					root: {
						"&.MuiCheckbox-sizeLarge": {
							"& svg": {
								transform: "scale(1.5)",
							},
						},
						"&.MuiCheckbox-sizeMedium": {
							"& svg": {
								transform: "scale(1.25)",
							},
						},
						"&.Mui-disabled": {
							opacity: 0.5,
							"&.MuiCheckbox-colorPrimary": {
								color: palette.primary.main,
							},
							"&.MuiCheckbox-colorSecondary": {
								color: color.Secondary,
							},
						},
					},
				},
			},
			MuiPagination: {
				styleOverrides: {
					root: {
						border: solidBorder(color.MonoChrome300),
						borderRadius: "50px",
						"& .MuiPaginationItem-root": {
							color: color.MonoChrome400,
							"&.Mui-selected": {
								color: palette.primary.main,
								backgroundColor: "transparent",
							},
						},
					},
				},
			},
			MuiPaginationItem: {
				styleOverrides: {
					root: {
						padding: "0px",
						margin: "0px",
					},
				},
			},
			MuiPopover: {
				styleOverrides: {
					root: {
						"& .MuiPaper-root": {
							border: solidBorder(color.MonoChrome300),
							borderRadius: 12,
							boxShadow: "none",
							backgroundColor: color.White,
							backgroundImage: "none",
						},
					},
				},
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						borderTopLeftRadius: "16px",
						borderTopRightRadius: "16px",
						maxHeight: "80%",
						backgroundColor: color.MonoChrome50,
						backgroundImage: "none",
					},
				},
			},
			SmallDrawer: {
				styleOverrides: {
					handle: {
						width: 36,
						height: 4,
						borderRadius: "2px",
						backgroundColor: scales.grey[300],
					},
					divider: {
						borderBottom: `1px solid ${scales.grey[100]}`,
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: {
						backgroundColor: color.White,
					},
					root: {
						"& .MuiOutlinedInput-root": {
							padding: "0px 0px 0px 12px",
						},
					},
					inputRoot: {
						"&.MuiInputBase-root": {
							borderRadius: "12px",
							backgroundColor: color.White,
						},
						"& .MuiOutlinedInput-input": {
							padding: "14px 12px",
							textAlign: "left",
							direction: "ltr",
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderRadius: "12px",
						},
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					root: {},
					paper: {
						boxShadow: "none",
						backgroundColor: color.White,
						borderRadius: "32px",
						padding: "0px",
						width: "60%",
						"@media (max-width: 900px)": {
							width: "90%",
							margin: "16px",
						},
					},
				},
			},
			MuiDialogContent: {
				defaultProps: {
					dividers: false,
				},
				styleOverrides: {
					root: {
						position: "relative",
						padding: "12px 16px",
					},
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						padding: "16px",
						paddingTop: "24px",
						fontSize: "16px",
						fontWeight: 500,
						lineHeight: "22.4px",
						gap: "16px",
						display: "flex",
						alignItems: "center",
					},
				},
			},
			MuiDialogActions: {
				styleOverrides: {
					root: {
						padding: "0 16px 24px 16px",
					},
				},
			},
			MuiToolbar: {
				styleOverrides: {
					root: {
						"&.MuiToolbar-gutters": {
							padding: "0px",
							minHeight: "0px",
						},
					},
				},
			},
			// @ts-expect-error-next-line TODO Fix later
			MuiCalendarPicker: {
				styleOverrides: {
					root: {
						margin: "0",
					},
				},
			},
			MuiPickersLayout: {
				styleOverrides: {
					contentWrapper: {
						alignItems: "center",
					},
				},
			},
		},
		colors: {
			border: {
				solid: (_color: string): string => {
					throw new Error("Function not implemented.");
				},
				main: "",
				hover: "",
				active: "",
				flatRowDivider: "",
			},
			placeholder: "",
			glassBackground: "",
			tabActiveBackground: "",
		},
		shadow: {
			primary: "",
			primaryHover: "",
			blueGlow: "",
			elevated: "",
			floating: "",
		},
		overlayScrollbar: {
			width: 0,
			inset: 0,
			color: "",
			radius: 0,
			fadeDuration: 0,
		},
		gradients: {
			primaryHero: "",
			realization: "",
			premium: "",
			dashboardSky: "",
			dashboardSkyGlow: "",
			iconBadge: "",
		},
		hero: {
			pillBg: "",
			tileBg: "",
			divider: "",
		},
		transition: {
			fast: "",
			slow: "",
		},
		borderRadius: "",
		vh: (_value: number): string => {
			throw new Error("Function not implemented.");
		},
		timeline: {
			height: {
				small: "",
				medium: "",
				large: "",
			},
		},
		iconSize: {
			xs: 0,
			sm: 0,
			md: 0,
			lg: 0,
		},
		spacingTokens: {
			none: 0,
			xxs: 0,
			xs: 0,
			sm: 0,
			md: 0,
			lg: 0,
			xl: 0,
			xxl: 0,
		},
		bottomSpacing: {
			nativeBottomBar: "",
			stickyCta: "",
			sheet: "",
		},
	};

	const customVariables = {
		colors: {
			placeholder: color.MonoChrome400,
			border: {
				solid: solidBorder,
				main: color.MonoChrome300,
				hover: color.MonoChrome400,
				active: palette.primary.main,
				flatRowDivider: solidBorder(alpha(color.MonoChrome100, 0.6)),
			},
			glassBackground: alpha(
				palette.background.paper,
				variant === "dark" ? 0.86 : 0.82,
			),
			tabActiveBackground: alpha(palette.blue[50], 0.8),
		},
		overlayScrollbar: {
			width: OVERLAY_SCROLLBAR_SIZE_PX,
			inset: 2,
			color: SCROLLBAR_THUMB_COLOR,
			radius: SCROLLBAR_RADIUS_PX,
			fadeDuration: 200,
		},
		shadow: {
			primary: "none",
			primaryHover: "0px 0px 15px 0px rgba(0, 0, 0, 0.15) inset",
			blueGlow: `0 4px 14px ${alpha(scales.blue[500], 0.32)}`,
			elevated: elevatedShadow,
			floating:
				"0 20px 25px rgba(0, 0, 0, 0.08), 0 8px 10px rgba(0, 0, 0, 0.04)",
		},
		gradients: {
			primaryHero:
				variant === "dark"
					? `linear-gradient(135deg, ${alpha(darken(palette.primary.main, 0.72), 0.95)} 0%, ${alpha(darken(palette.primary.main, 0.82), 0.98)} 100%)`
					: `linear-gradient(135deg, ${alpha(lighten(palette.primary.main, 0.92), 0.9)} 0%, ${alpha(lighten(palette.primary.main, 0.85), 0.95)} 100%)`,
			realization:
				variant === "dark"
					? "linear-gradient(135deg, #214A32, #123420)"
					: `linear-gradient(135deg, ${palette.realization.light} 0%, ${alpha(palette.realization.dark, 0.8)} 100%)`,
			premium:
				variant === "dark"
					? "linear-gradient(135deg, #244159, #232C48)"
					: `linear-gradient(135deg, ${scales.blue[500]}, ${scales.indigo[400]})`,
			avatar: `linear-gradient(135deg, ${palette.blue[500]}, ${palette.blue[700]})`,
			dashboardSky: `linear-gradient(180deg, ${surfaces.accent} 0%, ${scales.grey[50]} 15%)`,
			dashboardSkyGlow: `linear-gradient(180deg, ${surfaces.accent} 0%, transparent 100%)`,
			iconBadge: `linear-gradient(135deg, ${palette.blue[500]}, ${palette.blue[700]})`,
		},
		hero: {
			pillBg:
				variant === "light"
					? "rgba(255, 255, 255, 0.7)"
					: "rgba(255, 255, 255, 0.14)",
			tileBg:
				variant === "light"
					? "rgba(255, 255, 255, 0.8)"
					: "rgba(255, 255, 255, 0.1)",
			divider:
				variant === "light"
					? alpha(scales.grey[300], 0.4)
					: "rgba(255, 255, 255, 0.12)",
		},
		borderRadius: "5px",
		radii,
		chat: chatConfig,
		transition: {
			fast: "0.2s ease",
			slow: "0.45s ease",
		},
		vh: (val: number) => {
			return `calc(var(--vh, 1vh) * ${val})`;
		},
		iconSize,
		callout: {
			tinted: (color: CalloutColor): CalloutAppearance => {
				const tint = palette[color];

				return {
					card: {
						...calloutCardBase,
						borderRadius: radii.lg,
						backgroundColor: tint[50],
						borderColor: tint[100],
					},
					content: { alignItems: "flex-start", padding: "16px" },
					iconBox: { bg: tint[100], size: iconBoxSize.sm },
					icon: { size: iconSize.sm, color: tint.main },
					chevron: { size: iconSize.md, color: tint.main },
				};
			},
			gradient: (color: CalloutColor): CalloutAppearance => {
				const tint = palette[color];

				return {
					card: {
						...calloutCardBase,
						borderRadius: radii.xl,
						background: `linear-gradient(to bottom right, ${tint[50]}, ${alpha(palette.indigo[50], 0.8)})`,
						borderColor: alpha(tint[100], 0.7),
					},
					content: { alignItems: "center", padding: "14px 16px" },
					iconBox: {
						bg: alpha(palette.darkmodeText.white, 0.8),
						size: iconBoxSize.md,
					},
					icon: { size: iconSize.md, color: tint[500] },
					chevron: { size: iconSize.sm, color: palette.grey[400] },
				};
			},
		},
		// Named spacing scale. Values are MUI spacing multipliers (base = 10),
		// so `lg` (1.6) resolves to theme.spacing(1.6) = 16px.
		spacingTokens: {
			none: 0,
			xxs: 0.2, // 2px
			xs: 0.4, // 4px
			sm: 0.8, // 8px
			md: 1.2, // 12px
			lg: 1.6, // 16px
			xl: 2, // 20px
			xxl: 3.2, // 32px
		},
		bottomSpacing: {
			/**
			 * Space reserved at the bottom of a screen for the native bottom bar + safe-area
			 * inset. Driven entirely by the `--mobile-bottom-inset` CSS variable injected by
			 * the native shell, which knows whether a bar actually overlays the WebView
			 * (floating pilot bar) or not (legacy opaque bar below the WebView, standalone
			 * browser/PWA) — hence the fallback is 0.
			 */
			nativeBottomBar: "var(--mobile-bottom-inset, 0px)",
			stickyCta:
				"calc(var(--mobile-bottom-inset, env(safe-area-inset-bottom, 0px)) + 16px)",
			sheet: "24px",
		},
	};

	// TODO: fix later, probably theme missing some properties
	return createTheme(
		Object.assign(theme, customVariables) as any,
		csCZ,
	) as Theme;
};

export const getMyPlannTheme: GetThemeFunctionType = (variant, hspColor) => {
	const colorPalette = variant === "dark" ? DarkModeColors : Colors;
	const hspPalette: HspThemeColors = hspColor
		? {
				...colorPalette,
				hspColor: hspColor,
				hspColor75: lighten(hspColor, 0.25),
				hspColor25: lighten(hspColor, 0.75),
			}
		: colorPalette;
	return theme(hspPalette, variant);
};
